/**
 * Takes care of loading and storing of configs/settings.
 *
 * File: \src\components\login\LoginStorePinia.ts
 * Created Date: Thursday, April 28th 2022, 13:33:16
 * Author: Kircheva Boryana (HAU-EDCP)
 * -----
 * Last Modified: November 22nd 2024, 07:53:26
 * Modified By: Herrmann Sascha (LDC)
 * -----
 * Copyright (c) 2017-2022 Liebherr-Hausgeräte GmbH
 */
import { LogControllerApi, StoreUserSettingsUsingPUTRequest, UserControllerApi } from '@/api/apis';
import { LoggingConfig, UserSettings, UserSettingsLanguageEnum, UserSettingsSessionRenewEnum } from '@/api/models';

import MessageBus from '@/MessageBus';
import { cloneDeep, isEqual } from 'lodash';
import { defineStore } from 'pinia';
import LocalStorage from '../general/storage/LocalStorage';
import LocalStorageKeys from '../general/storage/LocalStorageKeys';
import { StateBoolean } from '@/composables/StateBoolean';

import { useApiStore } from '@/api/ApiStore';
import { useLoginStore } from '../login/LoginStore';
import { useI18nStore } from '@/I18nStore';

import { CubeLogger } from '@/Logger';

/**
 * Defines the store's state.
 *
 * @interface ConfigState
 */
interface ConfigState {
  userSettings: UserSettings | undefined;
  loggingConfig: LoggingConfig | undefined;
  userSettingsLoaded: StateBoolean;
}

/**
 * Stores configuration data and settings.
 *
 * @export
 * @class ConfigStore
 */
export const useConfigStore = defineStore('configStore', {
  state: (): ConfigState => ({
    userSettings: undefined,
    loggingConfig: undefined,
    userSettingsLoaded: { value: false }
  }),

  /**
   * The getters of the store.
   */
  getters: {
    /**
     * Checks whether all configs are loaded. Returns true is that's the case. No logging in this method!
     *
     * @return {*}  {boolean} true if all configs are loaded.
     */
    areConfigsLoaded(): boolean {
      return this.userSettings !== undefined && this.loggingConfig !== undefined;
    },

    /**
     * Gets the user settings. This is a clone of the original settings object.
     *
     * @return {*}  {UserSettings} The UserSettings.
     */
    getUserSettings(): UserSettings {
      return cloneDeep(this.userSettings);
    },

    /**
     * Getter for the logging config.
     *
     * @return {*}  {*} The copied logging config.
     */
    getLoggingConfig(): any {
      return this.loggingConfig;
    },

    /**
     * Getter for if user settings are loaded
     *
     * @return {*}  {StateBoolean} whether user was loaded or not.
     */
    getUserSettingsLoaded(): StateBoolean {
      return this.userSettingsLoaded;
    }
  },

  actions: {
    /**
     * This loads all the configs available. The logging config is loaded. Then the user config is loaded.
     *
     */
    loadAllConfigs(): void {
      if (this.userSettings === undefined) {
        this.loadUserSettings();
      }

      if (this.loggingConfig === undefined) {
        this.loadLoggingConfig();
      }
    },

    /**
     * Sets a new UserSettings object. If that is not equal to the existing state object, we save on the server and
     * save in state.
     *
     * @param {UserSettings} userSettings The new UserSettings object.
     */
    setUserSettings(userSettings: UserSettings): void {
      if (userSettings) {
        const clonedSettings: UserSettings = cloneDeep(userSettings);

        if (!isEqual(clonedSettings, this.userSettings)) {
          this.saveUserSettings(clonedSettings);
        }
      }
    },

    /**
     * Loads the user settings from the backend.
     *
     */
    loadUserSettings(): void {
      const apiStore = useApiStore();
      const userControllerApi: UserControllerApi = apiStore.getUserControllerApi;

      userControllerApi
        .getUserSettingsUsingGET()
        .then(response => {
          const newSettings: UserSettings = response;
          this.storeUserSettings(newSettings);
        })
        .catch(reason => {
          console.error('Cannot call the user settings', reason);
        })
        .finally(() => {
          this.applyUserSettings();
        });
    },

    /**
     * Saves the current user settings to the backend.
     *
     * @param {UserSettings} userSettings The UserSettings to save.
     */
    saveUserSettings(newUserSettings: UserSettings) {
      const logger = CubeLogger.$log.prefix('LoginStore::saveUserSettings');

      const apiStore = useApiStore();
      const userControllerApi: UserControllerApi = apiStore.getUserControllerApi;

      const request: StoreUserSettingsUsingPUTRequest = {
        userSettings: newUserSettings
      };

      userControllerApi
        .storeUserSettingsUsingPUT(request)
        .then(() => {
          logger.debug('Settings saved');
        })
        .catch(error => {
          logger.error('Cannot save user settings', error);
        })
        .finally(() => {
          this.storeUserSettings(newUserSettings);
          this.applyUserSettings();
        });
    },

    /**
     * Loads the logging config from the backend.
     *
     */
    loadLoggingConfig(): void {
      const logger = CubeLogger.$log.prefix('ConfigStore::loadLoggingConfig');

      const apiStore = useApiStore();
      const logControllerApi: LogControllerApi = apiStore.getLogControllerApi;

      logControllerApi.getLoggingConfigUsingGET().then(response => {
        const config: LoggingConfig = response;

        this.storeLoggingConfig(config);

        logger.debug('Initializing logger');
        CubeLogger.$log.init(config);
      });
    },

    /**
     * This is called after the user settings are loaded. We apply the settings (that should be applied
     * immediately).
     *
     */
    applyUserSettings(): void {
      const userSettings: UserSettings = this.userSettings;

      // Session renewal is a loginStore thing
      const renew: boolean = userSettings.sessionRenew === UserSettingsSessionRenewEnum.YES;
      const loginStore = useLoginStore();

      if (renew) {
        loginStore.startSilentRenew();
      } else {
        loginStore.stopSilentRenew();
      }

      const i18nStore = useI18nStore();

      // The language we also store in the localStorage to have it available before login
      const language: UserSettingsLanguageEnum = userSettings.language;
      const languageString: string = i18nStore.getLocaleLanguage(language);

      if (!i18nStore.isLanguageLoaded(languageString)) {
        i18nStore.loadLanguageString(languageString);
        LocalStorage.setItem(LocalStorageKeys.LOCAL_STORAGE_LANGUAGE_KEY, languageString);
      }
    },

    /**
     * Stores a new UserSettings instance in the state.
     *
     * @param {UserSettings} newSettings The new settings.
     */
    storeUserSettings(newSettings: UserSettings): void {
      const logger = CubeLogger.$log.prefix('ConfigStore::storeUserSettings');

      const checkEmit: boolean = this.userSettings === undefined;

      this.userSettings = newSettings;
      this.userSettingsLoaded.value = true;

      if (checkEmit && this.loggingConfig) {
        MessageBus.emitUserConfigured();
      }

      logger.debug('New user config settings in state', { usersettings: this.userSettings });
    },

    /**
     * Stores the given logging config in the state.
     *
     * @param {LoggingConfig} newConfig The config that should be stored.
     */
    storeLoggingConfig(newConfig: LoggingConfig): void {
      const logger = CubeLogger.$log.prefix('ConfigStore::storeLoggingConfig');

      this.loggingConfig = newConfig;

      if (this.userSettings && this.loggingConfig) {
        MessageBus.emitUserConfigured();
      }

      logger.debug('Logging config stored in state', newConfig);
    }
  }
});
